import React, { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import skyBg from '../assets/images/background/SkyWithClouds.png';
import skyBgMobile from '../assets/images/background/SkyWithCloudsMobile.png';
import rocksLeft from '../assets/images/background/RocksWithBird.png';
import rocksRight from '../assets/images/background/RocksWithGrass2.png';
import BeginInterviewIcon from './svg/BeginInterview';
import PrintInterviewIcon from './svg/PrintInterview';
import ScoringMethodIcon from './svg/ScoringMethod';
import ReferencesIcon from './svg/References';
import ChatbotSettingsIcon from './svg/ChatbotSettings';
import HelpSupportIcon from './svg/HelpSupport';
import Header from './Header';
import { CreditCardOutlined } from '@ant-design/icons';
import { useGetChatbotSettingsQuery } from '../redux/api/chatbotApi';
import { subscriptionStatus, subscriptionExpiry } from '../utils/data';
import { checkIsSubscribed } from '../utils/functions';
import { Tooltip } from 'antd';




interface HomeDashboardProps {
  currentUser: {
    fullname: string;
    role: string;
    email: string;
  };
  children?: React.ReactNode;
}

const HomeDashboard: FC<HomeDashboardProps> = ({ currentUser, children }) => {
  const navigate = useNavigate();
  const isAdmin = currentUser.role.toLowerCase() === 'admin';
  const isSubscribed = checkIsSubscribed(null, null, isAdmin);

  const { data: chatbotSettings } = useGetChatbotSettingsQuery();
  const settingsData = chatbotSettings ? (Array.isArray(chatbotSettings) ? chatbotSettings[0] : chatbotSettings) : null;
  const botName = settingsData?.name || 'Chatbot';

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/login';
  };

  return (
    <div className="relative min-h-screen w-full overflow-y-auto overflow-x-hidden flex flex-col bg-gradient-to-b from-[#a4c8e1] via-[#dceaf3] to-white font-['DM_Sans',sans-serif]">
      {/* Header */}
      <Header currentUser={currentUser} showAdmin={true} />


      {/* Main Content */}
      <main className="relative z-[5] flex-1 flex items-start justify-center px-[45px] md:px-[64px] py-[30px] md:pt-[110px] pb-[90px]">
        <div className="flex flex-col md:flex-row md:flex-wrap gap-[38px] md:gap-[46px] w-full max-w-[1350px] justify-center items-center mx-auto">
          {/* Card 1: Begin New Interview */}
          <Tooltip title={!isSubscribed ? "An active subscription is required to use this feature." : ""}>
            <div 
              className={`premium-gradient-card w-full max-w-[350px] md:max-w-[406px] h-[127px] md:h-[160px] flex flex-col items-center justify-center transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${isSubscribed ? 'cursor-pointer hover:-translate-y-1 hover:shadow-[0px_12px_32px_rgba(31,42,55,0.1)] group' : 'opacity-60 cursor-not-allowed grayscale'}`}
              onClick={() => {
                if (isSubscribed) {
                  navigate('/create-case-wizard');
                }
              }}
            >
              <div className={`mb-[21px] md:mb-[23px] flex items-center justify-center [&>svg]:w-[41px] [&>svg]:h-[37px] md:[&>svg]:w-[49px] md:[&>svg]:h-[46px] [&>svg]:transition-transform [&>svg]:duration-300 ${isSubscribed ? 'group-hover:[&>svg]:scale-110' : ''}`}>
                <BeginInterviewIcon />
              </div>
              <span className="font-['Lexend',sans-serif] text-[16px] md:text-[18px] font-medium text-[#1f2a37] text-center leading-[29px]">Begin New Interview</span>
            </div>
          </Tooltip>

          {/* Card 2: View/Print an existing Interview */}
          <div 
            className="premium-gradient-card w-full max-w-[350px] md:max-w-[406px] h-[127px] md:h-[160px] flex flex-col items-center justify-center cursor-pointer transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] hover:-translate-y-1 hover:shadow-[0px_12px_32px_rgba(31,42,55,0.1)] group" 

            onClick={() => navigate('/cases')}
          >
            <div className="mb-[21px] md:mb-[23px] flex items-center justify-center [&>svg]:w-[36px] [&>svg]:h-[39px] md:[&>svg]:w-[42px] md:[&>svg]:h-[46px] [&>svg]:transition-transform [&>svg]:duration-300 group-hover:[&>svg]:scale-110">
              <PrintInterviewIcon />
            </div>
            <span className="font-['Lexend',sans-serif] text-[16px] md:text-[18px] font-medium text-[#1f2a37] text-center leading-[29px]">View/Print an existing Interview</span>
          </div>

          {/* Admin Only Cards */}
          {isAdmin && (
            <>
              {/* Card: Scoring Method */}
              <div 
                className="premium-gradient-card w-full max-w-[350px] md:max-w-[406px] h-[127px] md:h-[160px] flex flex-col items-center justify-center cursor-pointer transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] hover:-translate-y-1 hover:shadow-[0px_12px_32px_rgba(31,42,55,0.1)] group" 
                onClick={() => navigate('/scoring-method')}
              >
                <div className="mb-[21px] md:mb-[23px] flex items-center justify-center [&>svg]:w-[38px] [&>svg]:h-[38px] md:[&>svg]:w-[45px] md:[&>svg]:h-[45px] [&>svg]:transition-transform [&>svg]:duration-300 group-hover:[&>svg]:scale-110">
                  <ScoringMethodIcon />
                </div>
                <span className="font-['Lexend',sans-serif] text-[16px] md:text-[18px] font-medium text-[#1f2a37] text-center leading-[29px]">Scoring Method</span>
              </div>

              {/* Card: References */}
              <div 
                className="premium-gradient-card w-full max-w-[350px] md:max-w-[406px] h-[127px] md:h-[160px] flex flex-col items-center justify-center cursor-pointer transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] hover:-translate-y-1 hover:shadow-[0px_12px_32px_rgba(31,42,55,0.1)] group" 
                onClick={() => navigate('/references')}
              >
                <div className="mb-[21px] md:mb-[23px] flex items-center justify-center [&>svg]:w-[38px] [&>svg]:h-[38px] md:[&>svg]:w-[45px] md:[&>svg]:h-[45px] [&>svg]:transition-transform [&>svg]:duration-300 group-hover:[&>svg]:scale-110">
                  <ReferencesIcon />
                </div>
                <span className="font-['Lexend',sans-serif] text-[16px] md:text-[18px] font-medium text-[#1f2a37] text-center leading-[29px]">References</span>
              </div>

              {/* Card: Chatbot Settings */}
              <div 
                className="premium-gradient-card w-full max-w-[350px] md:max-w-[406px] h-[127px] md:h-[160px] flex flex-col items-center justify-center cursor-pointer transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] hover:-translate-y-1 hover:shadow-[0px_12px_32px_rgba(31,42,55,0.1)] group" 
                onClick={() => navigate('/chatbot-settings')}
              >
                <div className="mb-[21px] md:mb-[23px] flex items-center justify-center [&>svg]:w-[38px] [&>svg]:h-[38px] md:[&>svg]:w-[45px] md:[&>svg]:h-[45px] [&>svg]:transition-transform [&>svg]:duration-300 group-hover:[&>svg]:scale-110">
                  <ChatbotSettingsIcon />
                </div>
                <span className="font-['Lexend',sans-serif] text-[16px] md:text-[18px] font-medium text-[#1f2a37] text-center leading-[29px]">{botName} Settings</span>
              </div>
            </>
          )}

          {/* Card 3: Subscription & Billing */}
          <div 
            className="premium-gradient-card w-full max-w-[350px] md:max-w-[406px] h-[127px] md:h-[160px] flex flex-col items-center justify-center cursor-pointer transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] hover:-translate-y-1 hover:shadow-[0px_12px_32px_rgba(31,42,55,0.1)] group" 

            onClick={() => navigate('/settings/billing')}
          >
            <div className="mb-[21px] md:mb-[23px] flex items-center justify-center [&>svg]:w-[38px] [&>svg]:h-[38px] md:[&>svg]:w-[45px] md:[&>svg]:h-[45px] [&>svg]:transition-transform [&>svg]:duration-300 group-hover:[&>svg]:scale-110">
              <CreditCardOutlined style={{ color: '#4682B4',fontSize:'34px',marginBottom:'10px' }} />
            </div>
            <span className="font-['Lexend',sans-serif] text-[16px] md:text-[18px] font-medium text-[#1f2a37] text-center leading-[29px]">Subscription & Billing</span>
          </div>

          {/* Card 4: Help & Support (Shown for standard users on dashboard; Admin can still access via URL) */}
          {!isAdmin && (
            <div 
              className="premium-gradient-card w-full max-w-[350px] md:max-w-[406px] h-[127px] md:h-[160px] flex flex-col items-center justify-center cursor-pointer transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] hover:-translate-y-1 hover:shadow-[0px_12px_32px_rgba(31,42,55,0.1)] group" 
              onClick={() => navigate('/support')}
            >
              <div className="mb-[21px] md:mb-[23px] flex items-center justify-center [&>svg]:w-[38px] [&>svg]:h-[38px] md:[&>svg]:w-[45px] md:[&>svg]:h-[45px] [&>svg]:transition-transform [&>svg]:duration-300 group-hover:[&>svg]:scale-110">
                <HelpSupportIcon />
              </div>
              <span className="font-['Lexend',sans-serif] text-[16px] md:text-[18px] font-medium text-[#1f2a37] text-center leading-[29px]">Help & Support</span>
            </div>
          )}
        </div>
      </main>

      {/* Dashboard Stats (children) */}
      {children && (
        <div className="relative z-[5]">
          {children}
        </div>
      )}

      {/* Bottom Assets */}
      <div className="relative w-full pointer-events-none z-[2] flex justify-between items-end mt-auto">
        <img src={rocksLeft} alt="" className="w-[209px] md:w-[220px] lg:w-[250px] xl:w-[280px] h-auto object-contain object-left-bottom" />
        <img src={rocksRight} alt="" className="w-[110px] md:w-[150px] lg:w-[180px] xl:w-[210px] h-auto object-contain object-right-bottom" />
      </div>

      {/* Background Assets */}
      <div className="absolute top-0 left-0 w-full z-[1] leading-none mix-blend-multiply opacity-80 pointer-events-none">
        <img src={skyBg} alt="" className="w-full h-auto object-cover md:min-h-[220px] hidden md:block" />
        <img src={skyBgMobile} alt="" className="w-full h-auto object-cover md:hidden" />
      </div>

    </div>
  );
};

export default HomeDashboard;
